import { useEffect, useState } from "react";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import moment from "moment";
import "moment/locale/pt-br";
import * as XLSX from "xlsx";

// Material UI imports
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonIcon from "@mui/icons-material/Person";
import PrintIcon from "@mui/icons-material/Print";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import LinkIcon from "@mui/icons-material/Link";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";

// DataGrid
import { DataGrid } from "@mui/x-data-grid";

// Configurar locale do moment
moment.locale("pt-br");

function RelatorioDocumentosEletronicosEmitidos({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
  filtrosAdicionais,
  atualizarFiltrosAdicionais,
  showFatura,
  unidadeSelecionada = "",
}) {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();
  const [dados, setDados] = useState(null);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedPair, setExpandedPair] = useState(null);

  // Este useEffect executa quando o componente é montado ou quando a ação muda
  useEffect(() => {
    if (acao) {
      if (acao === "gerar") {
        gerarRelatorio();
      }
    }
  }, [acao, atualizacoes, filtrosAdicionais]);

  const gerarRelatorio = async () => {
    if (!dadosClinica?._id) return;

    setError(null);
    startLoading();
    try {
      // Formatação das datas para a API
      const dataInicialFormatada = dataFiltro.dataInicial.format("YYYY-MM-DD");
      const dataFinalFormatada = dataFiltro.dataFinal.format("YYYY-MM-DD");

      // Preparar o corpo da requisição
      const requestBody = {
        dataInicio: dataInicialFormatada,
        dataFim: dataFinalFormatada,
        // Vazio = consolidado da empresa.
        ...(unidadeSelecionada && { estabelecimento: unidadeSelecionada }),
      };

      // Chamada para a API
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/documentoeletronico/getRelatorioDocumentosEmitidos/${dadosClinica._id}`,
        "POST",
        JSON.stringify(requestBody),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      const marcarDocumentosCancelados = (documentos, pairesCancelamento) => {
        return documentos.map((doc) => {
          const foiCancelado = pairesCancelamento?.some(
            (pair) => pair.documentoOriginal._id === doc._id,
          );
          return { ...doc, cancelado: foiCancelado };
        });
      };

      // Aplicar nos dados antes de setDados
      const dadosProcessados = {
        ...responseData.data,
        documentosEntrada: marcarDocumentosCancelados(
          responseData.data.documentosEntrada,
          responseData.data.pairesCancelamento,
        ),
        documentosParcela: marcarDocumentosCancelados(
          responseData.data.documentosParcela,
          responseData.data.pairesCancelamento,
        ),
      };

      setDados(dadosProcessados);
      // Limpar estados de UI
      setSearchTerm("");
    } catch (err) {
      console.error(
        "Erro ao gerar relatório de documentos eletrônicos emitidos:",
        err,
      );
      setError(
        err.message ||
          "Ocorreu um erro ao gerar o relatório de documentos eletrônicos emitidos.",
      );
    } finally {
      stopLoading();
    }
  };

  // Handler para mudança de aba (entrada vs parcela)
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchTerm("");
  };

  // Handler para pesquisa
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handler para expandir/recolher pares de cancelamento
  const handleExpandPair = (pairId) => {
    setExpandedPair(expandedPair === pairId ? null : pairId);
  };

  // Filtrar documentos de acordo com a pesquisa
  const filtrarDocumentos = (docs) => {
    if (!docs || !searchTerm) return docs;

    const termo = searchTerm.toLowerCase();

    return docs.filter((doc) => {
      if (
        doc.nrDocumento?.toLowerCase().includes(termo) ||
        doc.tipoDocumento?.toLowerCase().includes(termo) ||
        doc.emissor?.name?.toLowerCase().includes(termo) ||
        doc.cliente?.nome?.toLowerCase().includes(termo) ||
        doc.cliente?.nomeFiscal?.toLowerCase().includes(termo) ||
        doc.clienteFornecedor?.nome?.toLowerCase().includes(termo) ||
        doc.designacao?.toLowerCase().includes(termo) ||
        doc.documentoAnulado?.nrDocumento?.toLowerCase().includes(termo)
      ) {
        return true;
      }

      // Para documentos de cancelamento, o cliente está em pairesCancelamento
      if (doc.origem === "cancelamento") {
        const par = dados?.pairesCancelamento?.find(
          (pair) => pair.documentoCancelamento._id === doc._id,
        );
        if (par) {
          return (
            par.clienteFornecedor?.nome?.toLowerCase().includes(termo) ||
            par.cliente?.nome?.toLowerCase().includes(termo) ||
            par.cliente?.nomeFiscal?.toLowerCase().includes(termo)
          );
        }
      }

      return false;
    });
  };

  // Handler para imprimir documento
  // Handler para imprimir documento
  const handleImprimirDocumento = (documento) => {
    // ✅ MODIFICAR ESTA LINHA - incluir Event e DVE
    if (["NCE", "Event", "DVE"].includes(documento.tipoDocumento)) {
      // Procurar o par de cancelamento correspondente
      const parCancelamento = dados.pairesCancelamento?.find(
        (pair) => pair.documentoCancelamento._id === documento._id,
      );

      // Usar dados do cliente do documento original
      const clienteData =
        parCancelamento?.clienteFornecedor || parCancelamento?.cliente;

      showFatura({
        show: true,
        clinica: dadosClinica,
        cliente: {
          nome: parCancelamento?.clienteFornecedor
            ? clienteData?.nome
            : clienteData?.nomeFiscal ||
              clienteData?.nome ||
              "Cliente não especificado",
          endereco: clienteData?.endereco || "",
          contacto: clienteData?.contacto || "",
          nif: clienteData?.nif || "",
        },
        metodoPagamento: "",
        designacao: `Cancelamento de ${
          parCancelamento?.documentoOriginal?.tipoDocumento || "Documento"
        }`,
        valor: documento.valor,
        nrDocumento: documento.nrDocumento,
        dataDocumento: documento.dataEmissao,
        dataVencimento: null,
        entryName: documento.entryName,
        serie: documento.serie,
        tipoDocumento: documento.tipoDocumento,
        usuarioEmissor: documento.emissor?.name,
        referencia: parCancelamento?.documentoOriginal?.entryName,
        emitidoEmContingencia: documento.emitidoEmContingencia,
      });
      return;
    }

    // Resto do código permanece igual...
    const clienteData = documento.clienteFornecedor || documento.cliente;

    const foiAnulado = dados.pairesCancelamento?.some(
      (pair) => pair.documentoOriginal._id === documento._id,
    );

    showFatura({
      show: true,
      clinica: dadosClinica,
      cliente: {
        nome: documento.clienteFornecedor
          ? clienteData.nome
          : clienteData.nomeFiscal || clienteData.nome,
        endereco: clienteData.endereco,
        contacto: clienteData.contacto,
        nif: clienteData.nif,
      },
      metodoPagamento: documento.metodoPagamento,
      designacao: documento.designacao,
      valor: documento.valor,
      nrDocumento: documento.nrDocumento,
      dataDocumento: documento.dataEmissao,
      dataVencimento: documento.dataVencimento,
      entryName: documento.entryName,
      serie: documento.serie,
      tipoDocumento: documento.tipoDocumento,
      usuarioEmissor: documento.emissor?.name,
      dataPagamento:
        documento.origem === "entrada" ? documento.dataPagamento : null,
      anulado: foiAnulado,
      emitidoEmContingencia: documento.emitidoEmContingencia,
    });
  };

  const getNomeCliente = (doc) => {
    if (doc.origem === "cancelamento") {
      const par = dados?.pairesCancelamento?.find(
        (pair) => pair.documentoCancelamento._id === doc._id,
      );
      const c = par?.clienteFornecedor || par?.cliente;
      return par?.clienteFornecedor ? c?.nome : c?.nomeFiscal || c?.nome || "";
    }
    const c = doc.clienteFornecedor || doc.cliente;
    return doc.clienteFornecedor
      ? c?.nome
      : c?.nomeFiscal || c?.nome || "";
  };

  const exportarParaExcel = () => {
    if (!dados) return;

    const formatarData = (data) =>
      data ? moment(data).format("DD/MM/YYYY") : "";

    const formatarValor = (valor) =>
      typeof valor === "number" ? valor : 0;

    const prepararLinhas = (docs) =>
      docs.map((doc) => ({
        Tipo: doc.tipoDocumento || "",
        "Série/Número": `${doc.serie}/${doc.nrDocumento}`,
        "Data Emissão": formatarData(doc.dataEmissao),
        Cliente: getNomeCliente(doc),
        Descrição: doc.designacao || "",
        Emissor: doc.emissor?.name || "",
        "Valor (CVE)": formatarValor(doc.valor),
        "Entry Name": doc.entryName?.replace(".xml", "") || "",
        Contingência: doc.emitidoEmContingencia ? "Sim" : "Não",
      }));

    const ajustarColunas = (ws, linhas) => {
      if (!linhas.length) return;
      const chaves = Object.keys(linhas[0]);
      ws["!cols"] = chaves.map((k) => ({
        wch: Math.min(
          Math.max(k.length, ...linhas.map((r) => String(r[k] ?? "").length)) + 2,
          50,
        ),
      }));
    };

    const wb = XLSX.utils.book_new();

    const sheets = [
      { nome: "Parcelas", docs: filtrarDocumentos(dados.documentosParcela || []) },
      { nome: "Entradas", docs: filtrarDocumentos(dados.documentosEntrada || []) },
      { nome: "Cancelamentos", docs: filtrarDocumentos(dados.documentosCancelamento || []) },
    ];

    sheets.forEach(({ nome, docs }) => {
      if (!docs.length) return;
      const linhas = prepararLinhas(docs);
      const ws = XLSX.utils.json_to_sheet(linhas);
      ajustarColunas(ws, linhas);
      XLSX.utils.book_append_sheet(wb, ws, nome);
    });

    const termo = searchTerm.toLowerCase();
    const paresFiltrados = (dados.pairesCancelamento || []).filter((par) => {
      if (!termo) return true;
      const c = par.clienteFornecedor || par.cliente;
      const nomeCliente = par.clienteFornecedor
        ? c?.nome
        : c?.nomeFiscal || c?.nome || "";
      return (
        par.documentoOriginal.nrDocumento?.toLowerCase().includes(termo) ||
        par.documentoOriginal.tipoDocumento?.toLowerCase().includes(termo) ||
        par.documentoCancelamento.nrDocumento?.toLowerCase().includes(termo) ||
        par.documentoCancelamento.tipoDocumento?.toLowerCase().includes(termo) ||
        nomeCliente.toLowerCase().includes(termo) ||
        par.orcamentoCodigo?.toLowerCase().includes(termo)
      );
    });

    if (paresFiltrados.length) {
      const linhasPares = paresFiltrados.map((par) => {
        const c = par.clienteFornecedor || par.cliente;
        const nomeCliente = par.clienteFornecedor
          ? c?.nome
          : c?.nomeFiscal || c?.nome || "";
        return {
          "Doc. Original Tipo": par.documentoOriginal.tipoDocumento || "",
          "Doc. Original Série/Nº": `${par.documentoOriginal.serie}/${par.documentoOriginal.nrDocumento}`,
          "Doc. Cancelamento Tipo": par.documentoCancelamento.tipoDocumento || "",
          "Doc. Cancelamento Série/Nº": `${par.documentoCancelamento.serie}/${par.documentoCancelamento.nrDocumento}`,
          Cliente: nomeCliente,
          Origem: par.origem === "entrada" ? "Entrada" : "Parcela",
          Orçamento: par.orcamentoCodigo || "",
        };
      });
      const wsPares = XLSX.utils.json_to_sheet(linhasPares);
      ajustarColunas(wsPares, linhasPares);
      XLSX.utils.book_append_sheet(wb, wsPares, "Pares Cancelamento");
    }

    const inicio = dataFiltro.dataInicial.format("DD-MM-YYYY");
    const fim = dataFiltro.dataFinal.format("DD-MM-YYYY");
    XLSX.writeFile(wb, `DFEs_Emitidos_${inicio}_a_${fim}.xlsx`);
  };

  // Preparar colunas para DataGrid
  const columns = [
    {
      field: "tipoDocumento",
      headerName: "Tipo",
      width: 100,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Chip
            label={params.value}
            size="small"
            color={
              ["NCE", "Event", "DVE"].includes(params.value)
                ? "error"
                : "primary"
            }
            sx={{ fontWeight: "bold" }}
          />
        </Tooltip>
      ),
    },
    {
      field: "nrDocumento",
      headerName: "Número",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.serie}/{params.value}
        </Typography>
      ),
    },
    {
      field: "dataEmissao",
      headerName: "Data Emissão",
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2">
          {moment(params.value).format("DD/MM/YYYY")}
        </Typography>
      ),
    },
    {
      field: "cliente",
      headerName: "Cliente",
      flex: 1,
      renderCell: (params) => {
        let cliente, nomeExibir;

        // Para documentos de cancelamento, buscar cliente nos pares
        if (params.row.origem === "cancelamento") {
          const parCancelamento = dados.pairesCancelamento?.find(
            (pair) => pair.documentoCancelamento._id === params.row._id,
          );
          cliente =
            parCancelamento?.clienteFornecedor || parCancelamento?.cliente;
          nomeExibir = parCancelamento?.clienteFornecedor
            ? cliente?.nome
            : cliente?.nomeFiscal || cliente?.nome;
        } else {
          // Para outros documentos, usar dados diretos
          cliente = params.row.clienteFornecedor || params.row.cliente;
          nomeExibir = params.row.clienteFornecedor
            ? cliente?.nome
            : cliente?.nomeFiscal || cliente?.nome;
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PersonIcon
              fontSize="small"
              sx={{ mr: 1, color: "primary.main" }}
            />
            <Typography variant="body2">
              {nomeExibir || "Cliente não especificado"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "designacao",
      headerName: "Descrição",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {params.value || "Sem descrição"}
          </Typography>
          {params.row.documentoAnulado && (
            <Chip
              label={`Anula: ${params.row.documentoAnulado.tipoDocumento} ${params.row.documentoAnulado.nrDocumento}`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "emissor",
      headerName: "Emissor",
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value?.name || "Não especificado"}
        </Typography>
      ),
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <AttachMoneyIcon
            fontSize="small"
            sx={{ mr: 1, color: "success.main" }}
          />
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {params.value?.toLocaleString("pt-BR", {
              style: "currency",
              currency: "CVE",
            }) || "0$00"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 100,
      renderCell: (params) => (
        <Tooltip title="Imprimir Documento">
          <IconButton
            onClick={() => handleImprimirDocumento(params.row)}
            color="primary"
            size="small"
          >
            <PrintIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Renderização do componente
  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h5"
          gutterBottom
          component="div"
          sx={{ fontWeight: "bold" }}
        >
          Relatório de Documentos Eletrônicos Emitidos
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Período: {dataFiltro.dataInicial.format("DD/MM/YYYY")} a{" "}
            {dataFiltro.dataFinal.format("DD/MM/YYYY")}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Clínica: {dadosClinica?.nome || ""}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!dados && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {dados && dados.totalDocumentos === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Nenhum documento eletrônico emitido encontrado para o período
            selecionado.
          </Alert>
        )}

        {dados && dados.totalDocumentos > 0 && (
          <>
            {/* Cards de resumo */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: "success.light", height: "100%" }}>
                  <CardContent sx={{ textAlign: "center", color: "white" }}>
                    <Typography variant="h6" gutterBottom>
                      Total de Documentos
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircleIcon
                        sx={{ color: "white", mr: 1, fontSize: 30 }}
                      />
                      <Typography variant="h3" color="white">
                        {dados.totalDocumentos}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: "primary.light", height: "100%" }}>
                  <CardContent sx={{ textAlign: "center", color: "white" }}>
                    <Typography variant="h6" gutterBottom>
                      Documentos de Parcela
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AccountBalanceIcon
                        sx={{ color: "white", mr: 1, fontSize: 30 }}
                      />
                      <Typography variant="h3" color="white">
                        {dados.documentosParcela?.length || 0}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: "warning.light", height: "100%" }}>
                  <CardContent sx={{ textAlign: "center", color: "white" }}>
                    <Typography variant="h6" gutterBottom>
                      Documentos de Entrada
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ReceiptIcon
                        sx={{ color: "white", mr: 1, fontSize: 30 }}
                      />
                      <Typography variant="h3" color="white">
                        {dados.documentosEntrada?.length || 0}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: "error.light", height: "100%" }}>
                  <CardContent sx={{ textAlign: "center", color: "white" }}>
                    <Typography variant="h6" gutterBottom>
                      Documentos de Cancelamento
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CancelIcon
                        sx={{ color: "white", mr: 1, fontSize: 30 }}
                      />
                      <Typography variant="h3" color="white">
                        {dados.documentosCancelamento?.length || 0}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Tabs para Entrada vs Parcela vs Cancelamento */}
            <Box sx={{ mb: 2 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="fullWidth"
                aria-label="documentos eletronicos tabs"
              >
                <Tab
                  label={`Documentos de Parcela (${
                    dados.documentosParcela?.length || 0
                  })`}
                  icon={<AccountBalanceIcon />}
                  iconPosition="start"
                />

                <Tab
                  label={`Documentos de Entrada (${
                    dados.documentosEntrada?.length || 0
                  })`}
                  icon={<ReceiptIcon />}
                  iconPosition="start"
                />

                <Tab
                  label={`Documentos de Cancelamento (${
                    dados.documentosCancelamento?.length || 0
                  })`}
                  icon={<CancelIcon />}
                  iconPosition="start"
                />
                <Tab
                  label={`Pares de Cancelamento (${
                    dados.pairesCancelamento?.length || 0
                  })`}
                  icon={<LinkIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            {/* Pesquisa e Exportação */}
            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Pesquisar por número, tipo, emissor ou cliente..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<DownloadIcon />}
                  onClick={exportarParaExcel}
                >
                  Exportar Excel
                </Button>
              </Grid>
            </Grid>

            {/* Conteúdo da Tab Selecionada */}

            {tabValue === 0 && (
              /* Tab de Documentos de Parcela */
              <Box>
                {dados.documentosParcela &&
                dados.documentosParcela.length > 0 ? (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Documentos de Parcela ({dados.documentosParcela.length})
                      </Typography>
                      <Box sx={{ height: 600, width: "100%" }}>
                        <DataGrid
                          rows={filtrarDocumentos(dados.documentosParcela)}
                          columns={columns}
                          pageSize={10}
                          rowsPerPageOptions={[10, 25, 50]}
                          disableSelectionOnClick
                          getRowId={(row) => row._id}
                          getRowClassName={(params) =>
                            params.row.cancelado ? "row-cancelled" : ""
                          }
                          sx={{
                            "& .row-cancelled": {
                              backgroundColor: "rgba(158, 158, 158, 0.1)",
                              opacity: 0.8,
                              "& .MuiDataGrid-cell": {
                                textDecoration: "line-through",
                                color: "text.secondary",
                              },
                            },
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ) : (
                  <Alert severity="info">
                    Nenhum documento de parcela encontrado para o período
                    selecionado.
                  </Alert>
                )}
              </Box>
            )}

            {tabValue === 1 && (
              /* Tab de Documentos de Entrada */
              <Box>
                {dados.documentosEntrada &&
                dados.documentosEntrada.length > 0 ? (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Documentos de Entrada ({dados.documentosEntrada.length})
                      </Typography>
                      <Box sx={{ height: 600, width: "100%" }}>
                        <DataGrid
                          rows={filtrarDocumentos(dados.documentosEntrada)}
                          columns={columns}
                          pageSize={10}
                          rowsPerPageOptions={[10, 25, 50]}
                          disableSelectionOnClick
                          getRowId={(row) => row._id}
                          getRowClassName={(params) =>
                            params.row.cancelado ? "row-cancelled" : ""
                          }
                          sx={{
                            "& .row-cancelled": {
                              backgroundColor: "rgba(158, 158, 158, 0.1)",
                              opacity: 0.8,
                              "& .MuiDataGrid-cell": {
                                textDecoration: "line-through",
                                color: "text.secondary",
                              },
                            },
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ) : (
                  <Alert severity="info">
                    Nenhum documento de entrada encontrado para o período
                    selecionado.
                  </Alert>
                )}
              </Box>
            )}

            {tabValue === 2 && (
              /* Tab de Documentos de Cancelamento */
              <Box>
                {dados.documentosCancelamento &&
                dados.documentosCancelamento.length > 0 ? (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Documentos de Cancelamento (
                        {dados.documentosCancelamento.length})
                      </Typography>
                      <Box sx={{ height: 600, width: "100%" }}>
                        <DataGrid
                          rows={filtrarDocumentos(dados.documentosCancelamento)}
                          columns={columns}
                          pageSize={10}
                          rowsPerPageOptions={[10, 25, 50]}
                          disableSelectionOnClick
                          getRowId={(row) => row._id}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ) : (
                  <Alert severity="info">
                    Nenhum documento de cancelamento encontrado para o período
                    selecionado.
                  </Alert>
                )}
              </Box>
            )}

            {tabValue === 3 && (
              /* Tab de Pares de Cancelamento */
              <Box>
                {dados.pairesCancelamento &&
                dados.pairesCancelamento.length > 0 ? (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Pares de Cancelamento ({dados.pairesCancelamento.length}
                        )
                      </Typography>

                      {dados.pairesCancelamento.map((pair, index) => (
                        <Accordion
                          key={`pair-${index}`}
                          expanded={expandedPair === index}
                          onChange={() => handleExpandPair(index)}
                          sx={{ mb: 1 }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel-${index}-content`}
                            id={`panel-${index}-header`}
                          >
                            <Grid container alignItems="center" spacing={1}>
                              <Grid item xs={5}>
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <DescriptionIcon
                                    fontSize="small"
                                    sx={{ mr: 1, color: "primary.main" }}
                                  />
                                  <Typography sx={{ fontWeight: "bold" }}>
                                    {pair.documentoOriginal.tipoDocumento}{" "}
                                    {pair.documentoOriginal.serie}/
                                    {pair.documentoOriginal.nrDocumento}
                                  </Typography>
                                </Box>
                              </Grid>

                              <Grid item xs={2} sx={{ textAlign: "center" }}>
                                <Chip
                                  icon={<LinkIcon />}
                                  label="Cancelado por"
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                />
                              </Grid>

                              <Grid item xs={5}>
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <CancelIcon
                                    fontSize="small"
                                    sx={{ mr: 1, color: "error.main" }}
                                  />
                                  <Typography sx={{ fontWeight: "bold" }}>
                                    {pair.documentoCancelamento.tipoDocumento}{" "}
                                    {pair.documentoCancelamento.serie}/
                                    {pair.documentoCancelamento.nrDocumento}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </AccordionSummary>

                          <AccordionDetails>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <Card
                                  variant="outlined"
                                  sx={{ bgcolor: "primary.light" }}
                                >
                                  <CardContent>
                                    <Typography
                                      variant="subtitle1"
                                      gutterBottom
                                    >
                                      📄 Documento Original
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Tipo:</strong>{" "}
                                      {pair.documentoOriginal.tipoDocumento}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Número:</strong>{" "}
                                      {pair.documentoOriginal.serie}/
                                      {pair.documentoOriginal.nrDocumento}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Entry Name:</strong>{" "}
                                      {pair.documentoOriginal.entryName?.replace(
                                        ".xml",
                                        "",
                                      )}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Card
                                  variant="outlined"
                                  sx={{ bgcolor: "error.light" }}
                                >
                                  <CardContent>
                                    <Typography
                                      variant="subtitle1"
                                      gutterBottom
                                    >
                                      ❌ Documento de Cancelamento
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Tipo:</strong>{" "}
                                      {pair.documentoCancelamento.tipoDocumento}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Número:</strong>{" "}
                                      {pair.documentoCancelamento.serie}/
                                      {pair.documentoCancelamento.nrDocumento}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Entry Name:</strong>{" "}
                                      {pair.documentoCancelamento.entryName?.replace(
                                        ".xml",
                                        "",
                                      )}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>

                              <Grid item xs={12}>
                                <Card variant="outlined">
                                  <CardContent>
                                    <Typography
                                      variant="subtitle1"
                                      gutterBottom
                                    >
                                      👤 Informações do Cliente
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Cliente:</strong>{" "}
                                      {pair.clienteFornecedor
                                        ? pair.clienteFornecedor.nome
                                        : pair.cliente?.nomeFiscal ||
                                          pair.cliente?.nome ||
                                          "Não especificado"}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Origem:</strong>{" "}
                                      {pair.origem === "entrada"
                                        ? "Entrada"
                                        : "Parcela"}
                                    </Typography>
                                    {pair.orcamentoCodigo && (
                                      <Typography variant="body2">
                                        <strong>Orçamento:</strong>{" "}
                                        {pair.orcamentoCodigo}
                                      </Typography>
                                    )}
                                  </CardContent>
                                </Card>
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </CardContent>
                  </Card>
                ) : (
                  <Alert severity="info">
                    Nenhum par de cancelamento encontrado para o período
                    selecionado.
                  </Alert>
                )}
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}

export default RelatorioDocumentosEletronicosEmitidos;
